<template>
    <section class="ai-meeting-recorder" :class="{ 'ai-meeting-folded': isFolded, 'ai-meeting-idle': !jobId && !recording }">
        <!-- Folded (Keelan, 20 Oct): once the meeting is recording, the panel is one slim tab until it's opened. The
             panel below stays mounted (v-show) because the capture lives in it. -->
        <button v-if="isFolded" type="button" class="ai-meeting-tab" aria-expanded="false" @click="folded = false">
            <span class="ai-meeting-tab-dot"></span>
            <span class="ai-meeting-tab-text">Recording · {{ elapsedLabel }}</span>
            <span class="ai-meeting-tab-note">{{ error ? 'Problem saving' : uploading ? 'Saving…' : savedChunks ? savedChunks + ' chunk' + (savedChunks == 1 ? '' : 's') + ' saved' : 'saving each minute' }}</span>
            <span class="ai-meeting-tab-open">Open</span>
        </button>
        <div v-show="!isFolded" class="ai-meeting-body">
        <button v-if="recording && isControlled" type="button" class="ai-meeting-fold" title="Fold into a small tab (the recording keeps going)" @click="folded = true">Fold</button>
        <!-- Skip recording: chosen before Start Meeting; the run then starts without screen/mic capture or a recording job -->
        <label v-if="canChooseSkip" class="ai-meeting-skip"><input type="checkbox" v-model="skip" />Skip recording</label>
        <p v-if="skipped" class="ai-meeting-hint">{{ historyId ? "This meeting isn't being recorded." : !canRecord ? "This device can't record meetings, so Start Meeting starts it without a recording. To record it, start the meeting in Chrome or Edge on a computer." : "This meeting won't be recorded. Start Meeting won't ask to share your screen or microphone." }}</p>
        <template v-else>
        <ai-screen-recorder v-if="!finished || !jobId" ref="capture" streaming :hide-start="controlled && !historyId" @recording="recordingChanged" @chunk="addChunk" @recorded="stopped" />
        <label v-if="!jobId && !recording" class="ai-meeting-hint ai-meeting-speakers">Speakers
            <select v-model="speakerCount" aria-label="Number of speakers"><option :value="null">Detect automatically</option><option v-for="n in 30" :key="n" :value="n">{{ n }}</option></select>
        </label>
        <p v-if="!jobId && !recording" class="ai-meeting-hint ai-meeting-hint-line">Start Meeting shares your screen and mic. To include people on the call, pick <strong>Entire Screen</strong> and turn on <strong>Share with system audio</strong>.</p>
        <p v-if="jobId" class="ai-meeting-hint" aria-live="polite">{{ uploading ? 'Saving recording…' : savedChunks ? savedChunks + ' recording chunks saved' : 'Recording started — saving each minute' }}</p>
        <p v-if="error" class="ai-meeting-error" role="alert">{{ error }}</p>
        <router-link v-if="unfinishedJobId" :to="'/ai-review/' + unfinishedJobId">Open the unfinished recording</router-link>
        <button v-if="error && jobId" class="ai-btn ai-btn-ghost" @click="retry">Retry saving</button>
        <router-link v-if="jobId && finished && !pending.length" :to="'/ai-review/' + jobId" class="ai-btn">Open meeting notes</router-link>
        </template>
        </div>
    </section>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import AIScreenRecorder from './AIScreenRecorder.vue';
export default defineComponent({
    name: 'AIMeetingRecorder',
    components: { AiScreenRecorder: AIScreenRecorder },
    props: ['meetingId', 'historyId', 'controlled', 'runSkipped'],
    emits: ['busy', 'ready'],
    data() { return { folded: false, recStart: 0, elapsedSec: 0, elapsedTimer: 0 as any, skip: false, speakerCount: null as number | null, jobId: 0, recording: false, finished: false, uploading: false, finalizing: false, error: '', pending: [] as any[], nextSeq: 0, savedChunks: 0, historyReady: null as any, starting: null as any, flushPromise: null as any, dead: false }; },
    computed: {
        unfinishedJobId(): number { return Number(String(this.error).match(/\(job (\d+)\)/)?.[1]) || 0; },
        // Only before the run starts, only while nothing has been captured, and only where recording is possible. `controlled` is passed as a bare attribute
        // (Meetings.vue), which arrives as '' since the props are untyped, so check for its presence rather than truthiness.
        canChooseSkip(): boolean { return this.canRecord && this.isControlled && !this.historyId && !this.jobId && !this.recording && !this.finalizing; },
        // Skipped here before starting, or the running meeting was started that way (runSkipped, saved on the run,
        // so someone joining it sees the same thing)
        skipped(): boolean { return !!this.skip || (this.isControlled && !this.historyId && !this.canRecord) || (!!this.historyId && !!this.runSkipped); },
        // Screen capture exists in this browser (not on iPads/phones): without it Start Meeting runs the meeting unrecorded
        isControlled(): boolean { return this.controlled !== undefined && this.controlled !== false; },
        canRecord(): boolean { return !!(navigator.mediaDevices as any)?.getDisplayMedia; },
        // Folded only while a run is recording (not on the AI Assistant modal, where there's room)
        isFolded(): boolean { return this.folded && this.recording && this.isControlled; },
        elapsedLabel(): string {
            const m = Math.floor(this.elapsedSec / 60), sec = this.elapsedSec % 60;
            return `${m}:${String(sec).padStart(2, '0')}`;
        }
    },
    beforeUnmount() { this.dead = true; window.removeEventListener('beforeunload', this.warnUnload); clearInterval(this.elapsedTimer); },
    methods: {
        async startForMeeting(createHistory: any): Promise<boolean> {
            let resolveHistory: any;
            // Skipped: just start the run, the same as Meetings.vue does with no recorder (an error goes to its alert).
            // Nothing is captured, so finishBeforeEnd has nothing to wait for and no recording job is ever created.
            this.error = '';
            if (this.skip || !this.canRecord) {
                return !!(await createHistory(true));
            }
            this.historyReady = new Promise(resolve => { resolveHistory = resolve; });
            try {
                const started = await (this.$refs.capture as any)?.start();
                if (!started) {
                    resolveHistory(null);
                    // Cancelled at the permission coach: nothing was attempted, so no error
                    if ((this.$refs.capture as any)?.coachDismissed) return false;
                    // Picker cancelled or blocked: say so, or Start Meeting looks like it did nothing
                    if (!this.error) this.error = "The meeting hasn't started because the recording didn't start (screen sharing was cancelled or blocked). Click Start Meeting to try again, or tick Skip recording to run it without a recording.";
                    return false;
                }
                const context = await createHistory();
                resolveHistory(context);
                if (this.starting) await this.starting;
                return !this.error;
            } catch (e: any) {
                resolveHistory(null);
                this.error = e.response?.data || 'Could not start the meeting.';
                (this.$refs.capture as any)?.stop();
                return false;
            }
        },
        warnUnload(e: any) { if (this.recording || this.pending.length || this.finalizing) { e.preventDefault(); e.returnValue = ''; } },
        async recordingChanged(active: boolean) {
            this.recording = active; this.$emit('busy', active || this.pending.length > 0);
            // A run folds the panel into its tab as soon as recording starts (Keelan, 20 Oct); the tab's clock runs here
            clearInterval(this.elapsedTimer);
            if (active) {
                this.folded = this.isControlled; this.recStart = Date.now(); this.elapsedSec = 0;
                this.elapsedTimer = setInterval(() => { this.elapsedSec = Math.floor((Date.now() - this.recStart) / 1000); }, 1000);
            } else this.folded = false;
            if (!active || this.jobId) return;
            this.finished = false; this.error = '';
            window.addEventListener('beforeunload', this.warnUnload);
            this.starting = (async () => {
                    const context = this.historyReady ? await this.historyReady : { meetingID: this.meetingId || null, historyID: this.historyId || null };
                    if (!context) { (this.$refs.capture as any)?.stop(); return; }
                    const r = await axios.post(`${this.$store.state.apiUrl}/api/ai/meeting-recordings`, { ...context, speakerCount: this.speakerCount });
                    this.jobId = r.data.jobId;
                })()
                .catch(e => { this.error = e.response?.data || 'Could not start saving this recording.'; (this.$refs.capture as any)?.stop(); });
            await this.starting;
        },
        addChunk(blob: Blob) { this.pending.push({ seq: this.nextSeq++, blob }); this.flush().catch(() => { /* error shown beside recorder */ }); },
        async flush() {
            if (this.flushPromise) return this.flushPromise;
            this.flushPromise = (async () => {
                if (this.starting) await this.starting;
                if (!this.jobId) return;
                this.uploading = true;
                try {
                    while (this.pending.length && !this.dead) {
                        const c = this.pending[0]; let last: any;
                        for (let attempt = 0; attempt < 4; attempt++) {
                            try {
                                const data = new FormData(); data.append('seq', String(c.seq)); data.append('chunk', c.blob, 'minute.webm');
                                await axios.post(`${this.$store.state.apiUrl}/api/ai/meeting-recordings/${this.jobId}/chunk`, data, { timeout: 45000 }); last = null; break;
                            } catch (e) { last = e; if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt))); }
                        }
                        if (last) throw last;
                        this.pending.shift(); this.savedChunks = c.seq + 1;
                    }
                    this.error = '';
                } catch (e: any) {
                    this.error = e.response?.data || 'Recording upload was interrupted. Recording has stopped; keep this page open and retry saving.';
                    (this.$refs.capture as any)?.stop();
                    throw e;
                } finally { this.uploading = false; }
            })();
            try { await this.flushPromise; } finally { this.flushPromise = null; }
        },
        async stopped() {
            this.recording = false; this.finished = true; this.finalizing = true;
            try {
                await this.flush();
                if (!this.jobId) { this.pending = []; this.nextSeq = 0; return; }
                if (this.pending.length) return;
                await axios.post(`${this.$store.state.apiUrl}/api/ai/meeting-recordings/${this.jobId}/finish`, { expectedChunks: this.nextSeq });
                this.$emit('ready', this.jobId); this.$emit('busy', false);
            } catch (e: any) { this.error = e.response?.data || this.error || 'Could not finish saving. Keep this page open and retry.'; }
            finally { this.finalizing = false; }
        },
        async retry() { this.error = ''; await this.stopped(); },
        async finishBeforeEnd(ask = true): Promise<boolean> {
            if (this.recording) {
                if (ask && !confirm('Stop recording and save the meeting recording before ending this meeting?')) return false;
                (this.$refs.capture as any)?.stop();
                await new Promise(r => setTimeout(r, 50));
            }
            while (this.recording || this.finalizing) await new Promise(r => setTimeout(r, 100));
            return !this.jobId || (!this.error && !this.pending.length);
        }
    }
});
</script>
<style scoped>
.ai-meeting-recorder { background: #fff9f4; border: 1px solid #f3d6be; border-radius: 12px; padding: 8px 14px; color: #34261c; position: relative; }
.ai-meeting-hint { font-size: 12px; line-height: 1.4; margin: 4px 0 0; color: #796553; }
.ai-meeting-error { color: #a52c26; font-size: 13px; margin: 4px 0 0; }
.ai-meeting-skip { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: #34261c; cursor: pointer; margin: 0; }
.ai-meeting-skip input { width: 15px; height: 15px; margin: 0; accent-color: var(--primary-color); cursor: pointer; }
/* Before Start (Keelan, 20 Oct: half as thick): the choices on one row, the hint on the next */
.ai-meeting-idle .ai-meeting-body { display: flex; flex-wrap: wrap; align-items: center; column-gap: 18px; row-gap: 4px; }
.ai-meeting-idle .ai-meeting-body > :deep(.ai-rec) { display: contents; }
.ai-meeting-speakers { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 12.5px; color: #34261c; }
.ai-meeting-speakers select { margin: 0; padding: 2px 6px; font-size: 12.5px; width: auto; }
.ai-meeting-hint-line { flex: 1 1 420px; margin: 0; }
.ai-meeting-idle :deep(.ai-rec-float-pref) { margin: 0; font-size: 12.5px; }
.ai-meeting-idle :deep(.ai-rec-guide) { order: 11; margin: 0; font-size: 12px; }
.ai-meeting-idle :deep(.ai-rec-coach-link) { order: 10; margin: 0; font-size: 12px; }
.ai-meeting-idle :deep(.ai-rec-guide[open]) { flex-basis: 100%; }
/* The folded tab while a run records */
.ai-meeting-folded { padding: 0; border-color: #f0c9a8; background: #fff4ea; }
.ai-meeting-tab { display: flex; align-items: center; gap: 10px; width: 100%; padding: 6px 12px; border: none; background: none; font-family: inherit; font-size: 12.5px; color: #34261c; cursor: pointer; text-align: left; }
.ai-meeting-tab-dot { width: 9px; height: 9px; border-radius: 50%; background: #d7263d; box-shadow: 0 0 0 3px rgba(215, 38, 61, .18); animation: ai-meeting-blink 1.6s ease-in-out infinite; flex-shrink: 0; }
@keyframes ai-meeting-blink { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
.ai-meeting-tab-text { font-weight: 700; font-variant-numeric: tabular-nums; }
.ai-meeting-tab-note { color: #796553; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ai-meeting-tab-open { font-weight: 700; color: var(--primary-color); }
.ai-meeting-fold { position: absolute; top: 6px; right: 10px; border: 1px solid #f0c9a8; background: #fff; border-radius: 6px; font: 600 11.5px/1 inherit; font-family: inherit; padding: 4px 8px; color: #796553; cursor: pointer; }
.ai-meeting-fold:hover { color: #34261c; }
</style>
